import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RotateCcw } from 'lucide-react';
import { LowStockReportParams } from '@/app/types/lowStockReport';
import { listGodowns } from '@/app/services/inventoryService';
import { listSites } from '@/app/services/siteService';

interface LowStockFiltersProps {
  filters: LowStockReportParams;
  onFilterChange: (filters: Partial<LowStockReportParams>) => void;
}

export const LowStockFilters: React.FC<LowStockFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  // Fetch sites for filter
  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 }),
  });

  // Fetch godowns for filter
  const { data: godownsData } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 }),
  });

  const handleReset = () => {
    onFilterChange({
      site_id: undefined,
      godown_id: undefined,
      sort_by: 'shortage',
      sort_dir: 'desc',
    });
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Site Filter */}
          <div className="space-y-2">
            <Label htmlFor="site-filter">Site</Label>
            <Select
              value={filters.site_id?.toString() || 'all'}
              onValueChange={(value) =>
                onFilterChange({
                  site_id: value === 'all' ? undefined : parseInt(value),
                  godown_id: undefined, // Reset godown when site changes
                })
              }
            >
              <SelectTrigger id="site-filter">
                <SelectValue placeholder="All Sites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sitesData?.items.map((site) => (
                  <SelectItem key={site.id} value={site.id.toString()}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Godown Filter */}
          <div className="space-y-2">
            <Label htmlFor="godown-filter">Godown</Label>
            <Select
              value={filters.godown_id?.toString() || 'all'}
              onValueChange={(value) =>
                onFilterChange({
                  godown_id: value === 'all' ? undefined : parseInt(value),
                })
              }
              disabled={!!filters.site_id} // Disable if site is selected
            >
              <SelectTrigger id="godown-filter">
                <SelectValue placeholder="All Godowns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Godowns</SelectItem>
                {godownsData?.items
                  .filter(g => !filters.site_id || g.site_id === filters.site_id)
                  .map((godown) => (
                    <SelectItem key={godown.id} value={godown.id.toString()}>
                      {godown.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sort By */}
          <div className="space-y-2">
            <Label htmlFor="sort-filter">Sort By</Label>
            <Select
              value={filters.sort_by || 'shortage'}
              onValueChange={(value) =>
                onFilterChange({
                  sort_by: value as LowStockReportParams['sort_by'],
                })
              }
            >
              <SelectTrigger id="sort-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shortage">Shortage Quantity</SelectItem>
                <SelectItem value="item_name">Item Name</SelectItem>
                <SelectItem value="item_code">Item Code</SelectItem>
                <SelectItem value="godown_name">Godown Name</SelectItem>
                <SelectItem value="site_name">Site Name</SelectItem>
                <SelectItem value="current_quantity">Current Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort Direction */}
          <div className="space-y-2">
            <Label htmlFor="sort-dir-filter">Order</Label>
            <Select
              value={filters.sort_dir || 'desc'}
              onValueChange={(value) =>
                onFilterChange({
                  sort_dir: value as 'asc' | 'desc',
                })
              }
            >
              <SelectTrigger id="sort-dir-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Ascending</SelectItem>
                <SelectItem value="desc">Descending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reset Button */}
          <div className="flex items-end">
            <Button onClick={handleReset} variant="outline" className="w-full">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};